import C2ExtensionPopup, { type PopupVariant } from './Popup';

interface BrowserMockWithPopupProps {
  variant?: PopupVariant;
  initialName?: string;
}

export default function BrowserMockWithPopup({
  variant = 'create',
  initialName = 'vacation',
}: BrowserMockWithPopupProps) {
  return (
    <div className="relative">
      {/* Browser chrome */}
      <div
        className="bg-white relative overflow-visible"
        style={{
          borderRadius: 12,
          boxShadow: '0 30px 70px -25px rgba(20, 18, 35, 0.25)',
          border: '1px solid var(--color-hairline)',
        }}
      >
        {/* Tab bar */}
        <div
          className="flex items-center"
          style={{
            background: '#e6e6ea',
            padding: '12px 14px 0',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            gap: 8,
          }}
        >
          <div className="flex" style={{ gap: 7, marginRight: 14 }}>
            <div className="w-[12px] h-[12px] rounded-full" style={{ background: '#ff5f56' }} />
            <div className="w-[12px] h-[12px] rounded-full" style={{ background: '#ffbd2e' }} />
            <div className="w-[12px] h-[12px] rounded-full" style={{ background: '#27c93f' }} />
          </div>
          <div
            className="flex items-center bg-white"
            style={{
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              padding: '7px 16px 8px',
              fontSize: 13,
              color: 'var(--color-ink-soft)',
              gap: 8,
              maxWidth: 240,
            }}
          >
            <div
              className="rounded flex-shrink-0"
              style={{ width: 13, height: 13, background: 'var(--color-sky)' }}
            />
            <span className="whitespace-nowrap overflow-hidden text-ellipsis">
              Time off — Rippling
            </span>
            <span style={{ color: 'var(--color-ink-faint)', fontSize: 15 }}>×</span>
          </div>
          <div
            className="text-ink-muted"
            style={{ fontSize: 14, padding: '7px 12px' }}
          >
            +
          </div>
        </div>

        {/* Address bar */}
        <div
          className="flex items-center bg-white"
          style={{
            padding: '12px 16px',
            gap: 12,
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <span style={{ color: 'var(--color-ink-muted)', fontSize: 16 }}>←</span>
          <span style={{ color: 'var(--color-ink-faint)', fontSize: 16 }}>→</span>
          <span style={{ color: 'var(--color-ink-muted)', fontSize: 16 }}>↻</span>
          <div
            className="flex-1 flex items-center font-mono"
            style={{
              background: 'var(--color-bg-alt)',
              borderRadius: 999,
              padding: '8px 16px',
              gap: 9,
              fontSize: 13,
              color: 'var(--color-ink-soft)',
            }}
          >
            <span style={{ color: 'var(--color-mint-deep)' }}>🔒</span>
            <span>app.rippling.com/time-products/dashboard/time-off/summary</span>
          </div>
          <div className="flex items-center relative" style={{ gap: 8 }}>
            <div
              className="rounded flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                background: 'var(--color-bg-alt)',
                fontSize: 12,
              }}
            >
              ⋮
            </div>
            <div className="flex items-center justify-center relative" style={{ width: 32, height: 32 }}>
              <img
                src="/extension-icon128.png"
                alt="Tidy Go Links extension"
                width={32}
                height={32}
                style={{ display: 'block', borderRadius: 7 }}
              />
              {/* pointer arrow connecting to popup */}
              <div
                className="absolute"
                style={{
                  top: 34,
                  right: 6,
                  width: 12,
                  height: 12,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-hairline)',
                  borderRight: 'none',
                  borderBottom: 'none',
                  transform: 'rotate(45deg)',
                  zIndex: 3,
                }}
              />
            </div>
          </div>
        </div>

        {/* Fake Rippling time-off page (greyed out) */}
        <div
          className="bg-white"
          style={{
            padding: 0,
            minHeight: 540,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            opacity: 0.55,
            overflow: 'hidden',
            position: 'relative',
          }}
          aria-hidden="true"
        >
          <FakeRipplingTimeOff />
        </div>
      </div>

      {/* Popup anchored to extension icon */}
      <div
        className="absolute z-[2]"
        style={{
          top: 108,
          right: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
        aria-hidden="true"
      >
        <C2ExtensionPopup variant={variant} initialName={initialName} />
      </div>
    </div>
  );
}

function FakeRipplingTimeOff() {
  const block = (w: string, h: number, mb = 12) => (
    <div
      style={{
        width: w,
        height: h,
        background: 'var(--color-bg-alt)',
        borderRadius: 4,
        marginBottom: mb,
      }}
    />
  );

  return (
    <div className="flex" style={{ width: '100%', minHeight: 540 }}>
      {/* Side nav */}
      <div
        style={{
          width: 200,
          background: '#f7f6f3',
          padding: '20px 14px',
          borderRight: '1px solid var(--color-hairline)',
          flexShrink: 0,
        }}
      >
        <div className="flex items-center" style={{ gap: 8, marginBottom: 22 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: 'var(--color-ink-faint)',
            }}
          />
          <div
            style={{
              width: 80,
              height: 12,
              background: 'var(--color-ink-faint)',
              borderRadius: 4,
              opacity: 0.6,
            }}
          />
        </div>
        {[140, 110, 130, 100, 120, 95].map((w, i) => (
          <div key={i} className="flex items-center" style={{ gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 14,
                height: 14,
                background: 'var(--color-bg-alt)',
                borderRadius: 3,
              }}
            />
            <div
              style={{
                width: w,
                height: 10,
                background: 'var(--color-bg-alt)',
                borderRadius: 3,
              }}
            />
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1" style={{ padding: '28px 36px' }}>
        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              width: 160,
              height: 22,
              background: 'var(--color-bg-alt)',
              borderRadius: 5,
              marginBottom: 8,
            }}
          />
          <div
            style={{
              width: 280,
              height: 12,
              background: 'var(--color-bg-alt)',
              borderRadius: 4,
              opacity: 0.7,
            }}
          />
        </div>

        {/* Stats cards row */}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          {['12.5', '4.0', '8.5'].map((_, i) => (
            <div
              key={i}
              style={{
                border: '1px solid var(--color-hairline)',
                borderRadius: 8,
                padding: 18,
                background: '#fff',
              }}
            >
              {block('60%', 10, 10)}
              {block('40%', 24, 10)}
              {block('70%', 8, 0)}
            </div>
          ))}
        </div>

        {/* Section heading + button row */}
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <div
            style={{
              width: 180,
              height: 16,
              background: 'var(--color-bg-alt)',
              borderRadius: 4,
            }}
          />
          <div
            style={{
              width: 130,
              height: 32,
              background: '#cdd9ee',
              borderRadius: 6,
            }}
          />
        </div>

        {/* Table-like rows */}
        <div
          style={{
            border: '1px solid var(--color-hairline)',
            borderRadius: 8,
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns: '1.4fr 1fr 1fr 0.8fr',
              gap: 12,
              padding: '12px 18px',
              background: '#f7f6f3',
              borderBottom: '1px solid var(--color-hairline)',
            }}
          >
            {[80, 60, 70, 50].map((w, i) => (
              <div
                key={i}
                style={{
                  width: w,
                  height: 9,
                  background: 'var(--color-ink-faint)',
                  borderRadius: 3,
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
          {[0, 1, 2, 3].map(row => (
            <div
              key={row}
              className="grid"
              style={{
                gridTemplateColumns: '1.4fr 1fr 1fr 0.8fr',
                gap: 12,
                padding: '14px 18px',
                borderBottom: row < 3 ? '1px solid var(--color-hairline)' : 'none',
                alignItems: 'center',
              }}
            >
              {[160 + row * 8, 90, 110, 70].map((w, i) => (
                <div
                  key={i}
                  style={{
                    width: w,
                    height: 10,
                    background: 'var(--color-bg-alt)',
                    borderRadius: 3,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
